import { watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAutopilotStore } from '@llmor/autopilot-vue';
import { registerGlobalFunctions } from '@/autopilot/functions';
import { useUserStore } from '@/stores/user';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import type { IAutopilotConfig } from '@/types/autopilot';

/**
 * Main autopilot entrypoint.
 *
 * - Loads the global autopilot functions (see `src/autopilot/functions/`).
 * - Lazily connects the relay the first time the bar is opened.
 *
 * Must be called from a component setup (it relies on `useRouter()`).
 */

type AutopilotStore = ReturnType<typeof useAutopilotStore>;

// module-scoped guards so connect + registration only ever happen once per app load
let connectPromise: Promise<void> | null = null;
let functionsRegistered = false;
let reconnectWatchWired = false;
// the still-pending clean-slate listener for the connection currently being established
let pendingCleanSlateOff: (() => void) | null = null;

/**
 * Drop relay-replayed page keys this client does not currently hold. Snapshot is kept current
 * while disconnected so a navigation mid-outage is not wiped on the first sync after reconnect;
 * freeze once the socket is connected, before the replay lands.
 */
function armCleanSlate(store: AutopilotStore): void {
  pendingCleanSlateOff?.();
  const liveKeys = new Set(Object.keys(store.getContextSnapshot()));
  let frozen = false;

  const stopLiveWatch = watch(
    () => store.context,
    () => {
      if (frozen) return;
      liveKeys.clear();
      for (const key of Object.keys(store.context)) liveKeys.add(key);
    },
    { deep: true, flush: 'sync' }
  );

  const freeze = () => {
    if (frozen) return;
    frozen = true;
    stopLiveWatch();
  };

  const stopStatusWatch = watch(
    () => store.connectionStatus,
    (status) => {
      if (status === 'connected') freeze();
    },
    { flush: 'sync' }
  );

  const off = store.client.on('stateSynced', () => {
    freeze();
    off();
    pendingCleanSlateOff = null;
    stopStatusWatch();
    for (const key of Object.keys(store.context)) {
      if (!liveKeys.has(key)) delete store.context[key];
    }
  });
  pendingCleanSlateOff = () => {
    off();
    freeze();
    stopStatusWatch();
  };
}

function resolveAutopilotConfig(): IAutopilotConfig | null {
  // LEAD mints the conversation URL on the session. Do not probe GET /v1/config/wsconn here:
  // that endpoint 401s for this client and the HMAC client destroys the session on 401.
  return useUserStore().autopilot ?? null;
}

export function useAutopilot() {
  const store = useAutopilotStore();
  const router = useRouter();
  const userStore = useUserStore();

  // share the signed-in user (name + email) so the assistant knows who it is talking to.
  // a global key — published once and kept fresh as currentUser loads / changes.
  useAutopilotContext('current_user', () => {
    const user = userStore.currentUser;
    if (!user) return undefined;
    return {
      id: user.id,
      name: `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email,
      email: user.email,
      is_admin: user.is_admin,
    };
  });

  // idempotent: fetch config, register functions, connect the relay
  function ensureConnected(): Promise<void> {
    if (connectPromise) return connectPromise;

    connectPromise = (async () => {
      const autopilot = resolveAutopilotConfig();
      if (!autopilot) {
        // feature not enabled for this client — nothing to connect to
        return;
      }

      if (!functionsRegistered) {
        registerGlobalFunctions(store.client, { router });
        functionsRegistered = true;
      }

      // socket.io reconnects automatically, and each reconnect replays the relay's persisted
      // context — so re-arm the clean slate before every reconnect's first sync, not just the
      // initial connect. Wired once; the watcher lives for the app/store lifetime.
      if (!reconnectWatchWired) {
        reconnectWatchWired = true;
        watch(
          () => store.connectionStatus,
          (status, prev) => {
            // the context is still the clean live state at the moment the socket drops
            if (prev === 'connected' && status !== 'connected') armCleanSlate(store);
          }
        );
      }

      // clean the initial connection's replay
      armCleanSlate(store);

      try {
        await store.client.connect({
          conversationUrl: autopilot.host + autopilot.api_path,
        });
      } catch (e) {
        // connect failed before any sync — drop the pending arm so a retry registers a fresh one
        pendingCleanSlateOff?.();
        pendingCleanSlateOff = null;
        throw e;
      }
    })();

    // on failure, clear the guard so a later open can retry
    connectPromise.catch(() => {
      connectPromise = null;
    });

    return connectPromise;
  }

  function openBar() {
    // fire-and-forget — the overlay surfaces connection state/errors itself
    void ensureConnected();
    store.openBar();
  }

  function toggleBar() {
    if (store.isBarOpen) {
      store.closeBar();
      return;
    }
    openBar();
  }

  return { store, openBar, toggleBar, ensureConnected };
}
